import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";
import {
  ORGANIZATIONS_PAGE_LIMIT,
  OrganizationsDirectoryService,
  type OrganizationSummary,
} from "@/shared/services/organizations.service";

/**
 * Session-scoped `organization_id → name` directory.
 *
 * **Why this exists.** `GET /internal/users` returns `organization_id` and deliberately never joins the
 * `organization` relation, and the backend exposes no batch-by-ids endpoint and no organization search. So a
 * cross-organization table has to resolve names itself. The strategy is: preload the catalog once per
 * session, then fall back to one request per id the snapshot does not cover.
 *
 * - **Warm path — zero requests.** Once the catalog is in memory, every page render, filter change and
 *   pagination click resolves from a `Map`. The cache is module-scoped on purpose, so navigating away and
 *   back does not refetch.
 * - **Cold path — one request** for any catalog that fits `PAGE_CAP × 200` rows.
 * - **Fallback.** An id outside the snapshot costs one `GET /internal/organizations/:id`, deduplicated by id so
 *   twenty-five cells missing the same organization produce one request. It covers two real cases: a catalog
 *   larger than the cap — where, because the API orders by `created_at DESC`, the rows left out are the
 *   OLDEST organizations, i.e. the longest-tenured customers — and an organization created after the snapshot
 *   in a session that never reloads.
 *
 * **Negative results are typed and cached.** A `404` (soft-deleted organization) can never resolve, so it is
 * remembered for the session; a `403`/`5xx` is remembered as retryable so a transient failure does not pin
 * "unknown" forever. What is never done is collapsing them together, or with "this row has no organization":
 * the consumer must be able to tell "we could not find out" from "there is none".
 *
 * **No TTL.** Organization names change approximately never, and a periodic multi-page refetch would buy
 * refreshed renames nobody is waiting on. Call {@link invalidateOrganizationDirectory} after mutating an
 * organization instead.
 *
 * **Owner keying is tenancy, not caching.** Logging out does not reload the page, so without clearing on a
 * principal change the next operator in the same tab would read names the previous one could see.
 *
 * **Exit condition** — if the organization count ever approaches the user count, this whole mechanism is the
 * wrong shape: ask the backend for an `?include=organization` on the users listing and delete this file. The
 * tell is `total` climbing past a couple of thousand, which {@link UseOrganizationDirectoryResult.total}
 * exposes.
 */

/** Why an id could not be turned into a name. `deleted` is terminal; the others may succeed later. */
export type DirectoryUnresolvedReason = "deleted" | "forbidden" | "error";

export type DirectoryEntry =
  | { kind: "org"; name: string; isRoot: boolean }
  | { kind: "unresolved"; reason: DirectoryUnresolvedReason };

/** Stop paging the catalog after this many pages; the fallback covers whatever is left. */
const PAGE_CAP = 5;

const entries = new Map<string, DirectoryEntry>();
const inflight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

let ownerKey: string | null = null;
let version = 0;
let preloadStarted = false;
let preloading = false;
let truncated = false;
let preloadError = false;
let preloadDenied = false;
let total: number | null = null;
/**
 * Bumped whenever the store is reset. An in-flight walk compares against it before every write, so a response
 * that resolves after a re-login cannot repopulate the new operator's cache with the old one's rows. A
 * module-level epoch rather than a per-component `loadSeq`, because the walk is a singleton shared by every
 * consumer — one unmounting component must not abort it for the others.
 */
let epoch = 0;

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

let emitScheduled = false;

/**
 * Coalesces a burst of writes into one notification.
 *
 * The fallback path settles one promise per id, so a page of twenty-five distinct organizations — which is
 * precisely the truncated-catalog case, i.e. the longest-tenured customers — used to produce twenty-five
 * listener fan-outs, twenty-five renders, and twenty-five full recomputes of the 25-row view memo. The
 * responses land in the same microtask drain, so a single deferred emit covers all of them.
 *
 * Safe for `useSyncExternalStore`: `getSnapshot` reads `version`, which only moves inside {@link emit}, so
 * every render still sees a consistent pair of (version, store contents) — the notification is merely later.
 */
function scheduleEmit(): void {
  if (emitScheduled) return;
  emitScheduled = true;
  queueMicrotask(() => {
    emitScheduled = false;
    emit();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): number {
  return version;
}

function reset(): void {
  epoch += 1;
  entries.clear();
  inflight.clear();
  preloadStarted = false;
  preloading = false;
  truncated = false;
  preloadError = false;
  preloadDenied = false;
  total = null;
}

/** Drops the memo. Call after an organization is created, renamed or deleted. */
export function invalidateOrganizationDirectory(): void {
  reset();
  emit();
}

function classify(error: unknown): DirectoryUnresolvedReason {
  if (HttpApiService.isNotFoundError(error)) return "deleted";
  if (HttpApiService.isAccessDeniedError(error)) return "forbidden";
  return "error";
}

function absorb(rows: readonly OrganizationSummary[]): void {
  for (const row of rows) {
    entries.set(row.id, { kind: "org", name: row.name, isRoot: row.is_root });
  }
}

async function runPreload(): Promise<void> {
  const startedAt = epoch;
  preloading = true;
  preloadError = false;
  preloadDenied = false;
  emit();

  try {
    let skip = 0;
    for (let page = 0; page < PAGE_CAP; page += 1) {
      const result = await OrganizationsDirectoryService.listPage(skip, ORGANIZATIONS_PAGE_LIMIT);
      if (startedAt !== epoch) return;

      absorb(result.data);
      total = result.total;

      if (!result.hasMore || result.data.length === 0) {
        truncated = false;
        return;
      }
      skip += result.limit;
      // Exhausted the cap with rows still unread — disclose it; the fallback resolves what is visible.
      if (page === PAGE_CAP - 1) truncated = true;
    }
  } catch (error) {
    if (startedAt !== epoch) return;
    if (HttpApiService.isAccessDeniedError(error)) {
      // A denial is a fact about this principal's permissions; retrying it every render would be a storm
      // against an answer that cannot change within the session.
      preloadDenied = true;
    } else {
      preloadError = true;
      // A transient 5xx must NOT wedge the catalog for the rest of the session. Releasing the latch lets a
      // later `refresh()` — or a remount — try again, instead of silently degrading every page to one
      // request per organization forever.
      preloadStarted = false;
    }
  } finally {
    if (startedAt === epoch) {
      preloading = false;
      emit();
    }
  }
}

function ensurePreload(): void {
  if (preloadStarted) return;
  preloadStarted = true;
  void runPreload();
}

/** @returns Whether a request was actually started, so the caller can emit once per batch. */
function resolveOne(id: string): boolean {
  if (entries.has(id) || inflight.has(id)) return false;
  const startedAt = epoch;

  const promise = OrganizationsDirectoryService.getById(id)
    .then((org) => {
      if (startedAt !== epoch) return;
      entries.set(org.id, { kind: "org", name: org.name, isRoot: org.is_root });
    })
    .catch((error: unknown) => {
      if (startedAt !== epoch) return;
      entries.set(id, { kind: "unresolved", reason: classify(error) });
    })
    .finally(() => {
      if (startedAt !== epoch) return;
      inflight.delete(id);
      scheduleEmit();
    });

  inflight.set(id, promise);
  return true;
}

export interface UseOrganizationDirectoryResult {
  /** Pure read against the session cache. Never issues a request. */
  lookup: (id: string | null | undefined) => DirectoryEntry | undefined;
  /** True while this id has a request in flight — drives a per-cell skeleton on the fallback path. */
  isPending: (id: string | null | undefined) => boolean;
  /** Ask for these ids. At most one request per unknown id, deduplicated across every caller. */
  ensure: (ids: readonly (string | null | undefined)[]) => void;
  /**
   * Every organization currently in the cache, sorted by name — for a picker.
   *
   * This is the preloaded snapshot, so it is subject to the same cap as the directory itself: when
   * {@link UseOrganizationDirectoryResult.isTruncated} is true the list is the most recently created
   * `PAGE_CAP × 200`, not the whole catalog. Disclose that rather than presenting it as exhaustive.
   */
  organizations: OrganizationSummary[];
  /** True while the catalog preload is running — drives the column-level skeleton. */
  isPreloading: boolean;
  /** The catalog is larger than `PAGE_CAP × 200`; some ids will only resolve through the fallback. */
  isTruncated: boolean;
  hasError: boolean;
  accessDenied: boolean;
  /** Catalog size as last reported by the API, or `null` if it never answered. */
  total: number | null;
  /**
   * Bumped on every write to the store. Put it in the dependency array of a memo that derives from
   * {@link UseOrganizationDirectoryResult.lookup} — `lookup` is referentially stable, so without this a
   * resolved name would never reach a memoised view model.
   */
  version: number;
  refresh: () => void;
}

/**
 * @param enabled Pass `false` where the catalog is unreadable or pointless — an organization member gets a
 *   guaranteed 403 from `GET /internal/organizations` (it is `userTypes: [INTERNAL]`), and their listing is
 *   pinned to a single organization anyway. Disabled means no request is ever sent.
 */
export function useOrganizationDirectory(enabled: boolean = true): UseOrganizationDirectoryResult {
  const { user } = useAuth();
  const principalId = user?.id ?? null;

  const version = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Tenancy gate, evaluated on every read rather than mutated during render: until the effect below has
  // adopted this principal, every lookup answers "unknown". That is what makes it impossible for a cell to
  // paint the previous operator's organization names, even for one frame, without writing to a module
  // variable while rendering.
  const isOwner = ownerKey === principalId;
  // Read during render so the effect below re-runs after a reset. `epoch` moves ONLY in `reset()`, which is
  // always followed by an `emit()` — so an invalidation re-triggers the preload, while an ordinary store
  // write (a resolved id) does not. Depending on `version` here instead would turn a persistent catalog
  // failure into an infinite retry loop.
  const resetEpoch = epoch;

  useEffect(() => {
    if (!principalId) return;
    if (ownerKey !== principalId) {
      ownerKey = principalId;
      reset();
      emit();
    }
    if (enabled) ensurePreload();
  }, [enabled, principalId, resetEpoch]);

  // All three read mutable module state, so their RESULT changes with `version` even though their body does
  // not. Making the identity change with it is what lets a consumer memoise on the callback alone: without
  // this, a `useMemo` keyed on `lookup` would never recompute when a name finally resolves.
  const ensure = useCallback(
    (ids: readonly (string | null | undefined)[]) => {
      if (!enabled || !isOwner || preloading || preloadDenied) return;
      // One emit for the whole batch rather than one per id: a page of 25 distinct organizations used to
      // produce 25 synchronous listener fan-outs before the effect returned.
      let started = false;
      for (const id of ids) {
        if (id && resolveOne(id)) started = true;
      }
      if (started) emit();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the store's change signal
    [enabled, isOwner, version],
  );

  const lookup = useCallback(
    (id: string | null | undefined) => (isOwner && id ? entries.get(id) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the store's change signal
    [isOwner, version],
  );

  const isPending = useCallback(
    (id: string | null | undefined) => (isOwner && id ? inflight.has(id) : false),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the store's change signal
    [isOwner, version],
  );

  const refresh = useCallback(() => {
    invalidateOrganizationDirectory();
  }, []);

  const organizations = useMemo(() => {
    if (!enabled || !isOwner) return [];
    const out: OrganizationSummary[] = [];
    for (const [id, entry] of entries) {
      if (entry.kind === "org") out.push({ id, name: entry.name, is_root: entry.isRoot });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the store's change signal
  }, [enabled, isOwner, version]);

  return {
    lookup,
    isPending,
    ensure,
    organizations,
    // Before the principal is adopted nothing is known yet, which reads as "still resolving" rather than
    // "resolved to nothing" — the same unknown-is-not-empty rule the cell renders by.
    isPreloading: enabled && (preloading || !isOwner),
    isTruncated: isOwner && truncated,
    hasError: isOwner && preloadError,
    accessDenied: isOwner && preloadDenied,
    total: isOwner ? total : null,
    version,
    refresh,
  };
}
